import {
  Badge,
  Box,
  Card,
  CardBody,
  Center,
  Flex,
  Heading,
  Icon,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { type SourcingEventV2Dto, useListBidV2SForEventQuery } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../../../urls";
import { isBidContentUnsealed } from "../bidAccess";
import { effectiveBidDeadline } from "../qualification/stage";
import { isBidDeadlinePassed } from "../supplierRows";
import {
  deriveMinimumRequirementReview,
  type MinimumRequirementRow,
} from "./deriveMinimumRequirementReview";

interface Props {
  event: SourcingEventV2Dto;
}

export const MinimumRequirementsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const deadlinePassed = isBidDeadlinePassed(event);
  const unsealed = isBidContentUnsealed(event, selectedOrg);
  const { data, isLoading } = useListBidV2SForEventQuery(
    { eventId: event.id },
    { skip: !unsealed },
  );

  const bids = useMemo(
    () => (data?.bids ?? []).filter((bid) => bid.forNegotiationRound == null),
    [data],
  );
  const summary = useMemo(
    () => deriveMinimumRequirementReview(event.eventData.minimumRequirements ?? [], bids),
    [event.eventData.minimumRequirements, bids],
  );

  if (!unsealed) {
    const deadline = effectiveBidDeadline(event);
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10">
          <Flex direction="column" align="center" gap="3" textAlign="center">
            <Icon as={FiClock} boxSize="8" color="smPrimary" />
            <Text fontWeight="semibold" color="smText">
              {t("The competition is open for tenders")}
            </Text>
            {deadline && (
              <Text fontSize="sm" color="smMuted">
                {t("Deadline for tenders")}: <b>{displayDateWithTime(deadline)}</b>
              </Text>
            )}
            <Text fontSize="xs" color="smMuted" maxW="md">
              {t("The minimum requirements are checked once the deadline has passed and the bids are unsealed.")}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  if (summary.rows.length === 0) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10" textAlign="center">
          <Text color="smMuted">
            {deadlinePassed ? t("No bids were received.") : t("No bids delivered yet.")}
          </Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <Stack spacing="5">
      {!deadlinePassed && <EarlyAccessNote deadline={effectiveBidDeadline(event)} />}
      <Box>
        <Heading as="h3" size="sm" color="smText" mb="2">
          {summary.pending > 0
            ? t("{{reviewed}} of {{total}} bids fully reviewed", {
                reviewed: summary.rows.length - summary.pending,
                total: summary.rows.length,
              })
            : t("{{fulfilled}} fulfilled, {{notFulfilled}} not fulfilled", {
                fulfilled: summary.fulfilled,
                notFulfilled: summary.notFulfilled,
              })}
        </Heading>
        <ReviewTable eventId={event.id} rows={summary.rows} />
      </Box>
    </Stack>
  );
};

const EarlyAccessNote = ({ deadline }: { deadline: string | null }) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  return (
    <Flex align="center" gap="2" fontSize="sm" color="smMuted">
      <Icon as={FiClock} flexShrink={0} />
      <Text>
        {t("Bids delivered so far are open to you while the competition is still running.")}
        {deadline && (
          <>
            {" "}
            {t("Deadline for tenders")}: <b>{displayDateWithTime(deadline)}</b>
          </>
        )}
      </Text>
    </Flex>
  );
};

const ReviewTable = ({ eventId, rows }: { eventId: string; rows: MinimumRequirementRow[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
      <Table size="md">
        <Thead>
          <Tr>
            <Th>{t("Supplier")}</Th>
            <Th>{t("Requirements reviewed")}</Th>
            <Th>{t("Status")}</Th>
            <Th aria-hidden />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.bidId}
              cursor="pointer"
              _hover={{ bg: "smBackgroundSecondary" }}
              onClick={() =>
                navigate(
                  `${urls.v2.sourcingEvents.bidDetail.go(eventId, row.bidId)}?tab=minimumRequirements`,
                )
              }
            >
              <Td>
                <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
                  {row.supplierName}
                </Text>
              </Td>
              <Td
                fontSize="sm"
                color={row.reviewed === row.total ? "smText" : "smMuted"}
                fontFamily="mono"
              >
                {row.total > 0 ? `${row.reviewed}/${row.total}` : "–"}
              </Td>
              <Td>
                <StateBadge state={row.state} />
              </Td>
              <Td textAlign="right">
                <Icon as={FiChevronRight} color="smMuted" />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const StateBadge = ({ state }: { state: MinimumRequirementRow["state"] }) => {
  const { t } = useTranslation();
  if (state === "REJECTED") return <Badge colorScheme="red">{t("Rejected")}</Badge>;
  if (state === "NOT_FULFILLED") return <Badge colorScheme="red">{t("Not fulfilled")}</Badge>;
  if (state === "FULFILLED") return <Badge colorScheme="green">{t("Fulfilled")}</Badge>;
  return <Badge colorScheme="gray">{t("In progress")}</Badge>;
};
